// src/hooks/useHtmlDataTheme.js

import { useState, useEffect } from 'react';

// This helper function translates the specific attribute value into our simple 'light' or 'dark' string.
const getThemeFromAttribute = (attributeValue) => {
  if (attributeValue === 'deepechodark') {
    return 'dark';
  }
  // Default to light for 'deepecho' or any other value
  return 'light';
};

// Helper function to get system theme preference
const getSystemThemePreference = () => {
  if (typeof window === 'undefined') {
    return 'light'; // Default for SSR
  }
  
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  
  return 'light';
};

// Helper function to get theme with system fallback
const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light'; // Default for SSR
  }
  
  const currentAttribute = document.documentElement.getAttribute('data-theme');
  
  // If there's already a theme attribute set, use it
  if (currentAttribute) {
    return getThemeFromAttribute(currentAttribute);
  }
  
  // Otherwise, check if there's a theme in localStorage
  const storedTheme = localStorage.getItem('theme');
  if (storedTheme) {
    return getThemeFromAttribute(storedTheme);
  }
  
  // Finally, fall back to system preference
  return getSystemThemePreference();
};

export const useHtmlDataTheme = () => {
  // 1. Initialize state by reading the attribute directly or falling back to system theme.
  // The function inside useState ensures this only runs once on the initial render.
  const [theme, setTheme] = useState(() => {
    return getInitialTheme();
  });

  useEffect(() => {
    // Ensure this effect runs only in the browser.
    if (typeof window === 'undefined') {
      return;
    }

    // 2. Create a MutationObserver to watch for attribute changes on the <html> element.
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newThemeValue = document.documentElement.getAttribute('data-theme');
          setTheme(getThemeFromAttribute(newThemeValue));
        }
      }
    });

    // 3. Start observing the <html> tag for changes to its attributes.
    observer.observe(document.documentElement, {
      attributes: true, // We are interested in attribute changes
      attributeFilter: ['data-theme'] // Specifically, only the 'data-theme' attribute
    });

    // 4. Cleanup: Disconnect the observer when the component unmounts.
    // This is crucial to prevent memory leaks.
    return () => {
      observer.disconnect();
    };
  }, []); // The empty dependency array ensures this effect runs only once (on mount).

  return theme; // The hook returns the current theme: 'light' or 'dark'.
};